// ABOUTME: Configuration step for report wizard - practical report settings.
// ABOUTME: Clean UI with custom styled controls, no native checkboxes/radios.

import { useMemo } from 'react';
import {
  FileText,
  BarChart3,
  MessageSquare,
  Table,
  Pin,
  Check,
  FileCheck,
  BookOpen,
  ScrollText,
  ChevronDown,
  Pencil,
} from 'lucide-react';
import { Input } from '@/components/ui/Input';
import type { Pin as PinType, InvestigationListItem } from '@/types/api';

export interface ReportConfig {
  title: string;
  length: '1-2' | '3-5' | '6+';
  tone: 'executive' | 'technical' | 'conversational';
  includeExecutiveSummary: boolean;
  includeMethodology: boolean;
  includeRecommendations: boolean;
  includeVisuals: boolean;
  customPrompt: string;
}

interface WizardConfigStepProps {
  config: ReportConfig;
  onChange: (config: ReportConfig) => void;
  selectedPins: PinType[];
  selectedInvestigations: InvestigationListItem[];
  onEditContext?: () => void;
}

// Get icon for pin type
function getPinIcon(type: PinType['type']) {
  switch (type) {
    case 'chart':
      return <BarChart3 className="w-3 h-3" />;
    case 'table':
      return <Table className="w-3 h-3" />;
    default:
      return <FileText className="w-3 h-3" />;
  }
}

export function WizardConfigStep({
  config,
  onChange,
  selectedPins,
  selectedInvestigations,
  onEditContext,
}: WizardConfigStepProps) {
  const totalPins = selectedPins.length;
  const totalInvestigations = selectedInvestigations.length;

  // Group pins by investigation for the details view
  const pinsGroupedByInvestigation = useMemo(() => {
    const grouped: Record<string, { investigation: InvestigationListItem | null; pins: PinType[] }> = {};
    for (const pin of selectedPins) {
      const invId = pin.source_investigation_id;
      if (!grouped[invId]) {
        const inv = selectedInvestigations.find(i => i.id === invId) || null;
        grouped[invId] = { investigation: inv, pins: [] };
      }
      grouped[invId].pins.push(pin);
    }
    return Object.values(grouped);
  }, [selectedPins, selectedInvestigations]);

  const investigationsWithoutSelectedPins = selectedInvestigations.filter(
    inv => !selectedPins.some(pin => pin.source_investigation_id === inv.id)
  );

  const lengthOptions = [
    { value: '1-2' as const, label: '1-2 pages', desc: 'Quick summary' },
    { value: '3-5' as const, label: '3-5 pages', desc: 'Standard' },
    { value: '6+' as const, label: '6+ pages', desc: 'Comprehensive' },
  ];

  const toneOptions = [
    { value: 'executive' as const, label: 'Executive', desc: 'Strategic, high-level' },
    { value: 'technical' as const, label: 'Technical', desc: 'Detailed analysis' },
    { value: 'conversational' as const, label: 'Casual', desc: 'Easy to read' },
  ];

  const sectionOptions = [
    { key: 'includeExecutiveSummary' as const, label: 'Executive Summary', desc: 'TL;DR at top', icon: FileCheck },
    { key: 'includeRecommendations' as const, label: 'Recommendations', desc: 'Action items', icon: BookOpen },
    { key: 'includeMethodology' as const, label: 'Methodology', desc: 'How it was done', icon: ScrollText },
    { key: 'includeVisuals' as const, label: 'Charts & Tables', desc: 'From pins', icon: BarChart3 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-medium text-surface-800">
          Configure Report
        </h2>
        <p className="text-sm text-surface-600 mt-0.5">
          Set up how your report should be generated.
        </p>
      </div>

      {/* Selected Context - At top, collapsible */}
      {(totalPins > 0 || totalInvestigations > 0) && (
        <details className="group">
          <summary className="flex items-center gap-3 cursor-pointer list-none">
            <span className="text-sm font-medium text-surface-800">Selected Context</span>
            <div className="flex items-center gap-2">
              {totalInvestigations > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-accent-50 text-accent-600 text-xs">
                  <MessageSquare className="w-3 h-3" />
                  {totalInvestigations}
                </span>
              )}
              {totalPins > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-primary-50 text-primary-600 text-xs">
                  <Pin className="w-3 h-3" />
                  {totalPins}
                </span>
              )}
              <ChevronDown className="w-4 h-4 text-surface-500 group-open:rotate-180 transition-transform" />
            </div>
            {onEditContext && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onEditContext();
                }}
                className="ml-auto flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs text-surface-500 hover:text-primary-600 hover:bg-surface-50 transition-colors cursor-pointer"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            )}
          </summary>
          <div className="mt-3 p-3 rounded-sm bg-surface-50 border border-surface-200 space-y-2 max-h-36 overflow-y-auto">
            {/* Investigations without pins (full context) */}
            {investigationsWithoutSelectedPins.map(inv => (
              <div key={inv.id} className="flex items-center gap-2 text-xs">
                <MessageSquare className="w-3.5 h-3.5 text-accent-600 flex-shrink-0" />
                <span className="text-surface-800 truncate flex-1">{inv.title}</span>
                <span className="text-surface-500 px-1.5 py-0.5 rounded-sm bg-surface-100 text-[10px]">full context</span>
              </div>
            ))}
            {/* Pins grouped by investigation */}
            {pinsGroupedByInvestigation.map((group, idx) => (
              <div key={idx} className="space-y-1.5">
                {group.investigation && (
                  <div className="flex items-center gap-2 text-xs text-surface-600">
                    <MessageSquare className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{group.investigation.title}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 pl-5">
                  {group.pins.map(pin => (
                    <span
                      key={pin.id}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-primary-50 text-primary-600 text-[11px]"
                    >
                      {getPinIcon(pin.type)}
                      <span className="truncate max-w-32">{pin.title}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Report Title */}
      <div>
        <label className="block text-sm font-medium text-surface-800 mb-2">
          Report Title
        </label>
        <Input
          placeholder="e.g., Q4 Sales Analysis Report"
          value={config.title}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
        />
      </div>

      {/* Custom Instructions */}
      <div>
        <label className="block text-sm font-medium text-surface-800 mb-1.5">
          What should this report focus on?
        </label>
        <p className="text-xs text-surface-500 mb-2">
          Describe the story you want the report to tell.
        </p>
        <textarea
          placeholder="e.g., Focus on revenue trends from the pinned charts. Explain why Q4 dropped 15% vs Q3 and suggest next steps."
          value={config.customPrompt}
          onChange={(e) => onChange({ ...config, customPrompt: e.target.value })}
          rows={2}
          className="w-full px-4 py-3 bg-surface-0 border border-surface-200 rounded-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-600/25 focus:border-primary-600 resize-none text-sm"
        />
      </div>

      {/* Length & Tone - Side by side */}
      <div className="grid grid-cols-2 gap-6">
        {/* Report Length */}
        <div>
          <label className="block text-sm font-medium text-surface-800 mb-3">
            Length
          </label>
          <div className="flex gap-2">
            {lengthOptions.map(({ value, label, desc }) => (
              <button
                key={value}
                type="button"
                onClick={() => onChange({ ...config, length: value })}
                className={`flex-1 flex flex-col items-center gap-1 px-3 py-3 rounded-sm border transition-all cursor-pointer ${
                  config.length === value
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-surface-200 bg-surface-0 hover:bg-surface-50 hover:border-surface-300'
                }`}
              >
                <span className={`text-sm font-medium ${
                  config.length === value ? 'text-primary-600' : 'text-surface-800'
                }`}>
                  {label}
                </span>
                <span className="text-[10px] text-surface-500">{desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tone */}
        <div>
          <label className="block text-sm font-medium text-surface-800 mb-3">
            Writing Style
          </label>
          <div className="flex gap-2">
            {toneOptions.map(({ value, label, desc }) => (
              <button
                key={value}
                type="button"
                onClick={() => onChange({ ...config, tone: value })}
                className={`flex-1 flex flex-col items-center gap-1 px-3 py-3 rounded-sm border transition-all cursor-pointer ${
                  config.tone === value
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-surface-200 bg-surface-0 hover:bg-surface-50 hover:border-surface-300'
                }`}
              >
                <span className={`text-sm font-medium ${
                  config.tone === value ? 'text-primary-600' : 'text-surface-800'
                }`}>
                  {label}
                </span>
                <span className="text-[10px] text-surface-500">{desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sections to Include */}
      <div>
        <label className="block text-sm font-medium text-surface-800 mb-3">
          Include Sections
        </label>
        <div className="grid grid-cols-2 gap-2">
          {sectionOptions.map(({ key, label, desc, icon: Icon }) => {
            const isSelected = config[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => onChange({ ...config, [key]: !isSelected })}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm border transition-all text-left cursor-pointer ${
                  isSelected
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-surface-200 bg-surface-0 hover:bg-surface-50 hover:border-surface-300'
                }`}
              >
                {/* Custom checkbox */}
                <div className={`w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected
                    ? 'bg-primary-600'
                    : 'bg-surface-100 border border-surface-200'
                }`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                <Icon className={`w-4 h-4 flex-shrink-0 ${
                  isSelected ? 'text-primary-600' : 'text-surface-500'
                }`} />
                <div className="min-w-0">
                  <div className={`text-sm font-medium ${
                    isSelected ? 'text-surface-800' : 'text-surface-600'
                  }`}>
                    {label}
                  </div>
                  <div className="text-[10px] text-surface-500 truncate">{desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
